import { useState, useEffect, useRef } from 'react';
import { XMarkIcon, ChevronDownIcon, PlusIcon } from '@heroicons/react/24/outline';
import type { Entry, Project, ProjectTask } from '../../services/api/timeEntry';
import { getProjectTasks } from '../../services/api/timeEntry';
import { formatTaskIdentifier } from '../../utils/taskFormat';
import { parseDecimalInput } from '../../utils/parseDecimalInput';
import { ApiClientError } from '../../services/api/client';
import { PROJECT_MONTH_LOCKED_CODE } from '../../services/api/projectMonthLocks';
import { DateField } from '../ui/DateField';

interface EntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    projectId: number;
    date: string;
    hours: number;
    description?: string;
    task?: string;
  }) => Promise<void>;
  onDelete?: () => Promise<void>;
  entry?: Entry | null;
  date?: string;
  projectId?: number;
  projects: Project[];
  // Pre-filled data from copy/paste
  initialHours?: number;
  initialDescription?: string | null;
  initialTask?: string | null;
  /**
   * Phase 113 (MKS-208, SC3) — offered when the save is refused because the
   * project-month is locked. The dialog hands over what the user was already
   * typing so filing the request costs them one more field, not a re-entry.
   * Absent while the release flag is off, which hides the CTA entirely.
   */
  onRequestBackdatedHours?: (prefill: {
    projectId: number;
    date: string;
    hours?: number;
    task?: string | null;
    description?: string | null;
  }) => void;
  /**
   * MKS-388 — the save is refused up front (the caller holds a Claude seat
   * with no connected Monka). Disables Save and says why; Delete and Cancel
   * stay live, because the gate refuses writing time, not removing it. False
   * while the release flag is off.
   */
  saveDisabled?: boolean;
  /** The one-line reason shown next to a disabled Save. */
  saveDisabledReason?: string;
  /**
   * MKS-536 — this entry sits in a locked project-month and the hours-neutral
   * move exemption is on. The move itself is allowed, so project, task and
   * description stay editable; hours and the date are frozen, because those are
   * exactly what would change the month's reported total, and the backend
   * refuses them. False while the release flag is off, where the dialog is
   * never reached for a locked month at all.
   */
  lockedMonthMoveOnly?: boolean;
}

export function EntryModal({
  isOpen,
  onClose,
  onSave,
  onDelete,
  entry,
  date: initialDate,
  projectId: initialProjectId,
  projects,
  initialHours,
  initialDescription,
  initialTask,
  onRequestBackdatedHours,
  saveDisabled = false,
  saveDisabledReason,
  lockedMonthMoveOnly = false,
}: EntryModalProps) {
  const [projectId, setProjectId] = useState<number>(initialProjectId || 0);
  const [date, setDate] = useState(initialDate || '');
  const [hours, setHours] = useState('');
  const [description, setDescription] = useState('');
  // Phase 28 / MKS-115 — `task` holds the bare name (sent to BE,
  // findOrCreateTask-by-name contract); `taskDisplay` holds the formatted
  // `#code — name` string shown in the input.
  const [task, setTask] = useState('');
  const [taskDisplay, setTaskDisplay] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // Phase 113 (MKS-208, SC3) — set when the last save was refused by the
  // project-month lock specifically, which is what turns the generic error
  // banner into an offer to file a backdated-hours request.
  const [monthLocked, setMonthLocked] = useState(false);

  // Task dropdown state
  const [availableTasks, setAvailableTasks] = useState<ProjectTask[]>([]);
  // MKS-220 — which project `availableTasks` actually belongs to. The list is
  // fetched asynchronously, so until this equals `projectId` the array still
  // holds the PREVIOUS project's tasks and must not be used to decide whether
  // a carried-over task exists on the new project.
  const [tasksProjectId, setTasksProjectId] = useState<number | null>(null);
  // MKS-220 — the task string the dialog carried into a project switch. Null
  // when the user has not switched projects, or has typed since.
  const carriedTaskRef = useRef<string | null>(null);
  const [isTaskDropdownOpen, setIsTaskDropdownOpen] = useState(false);
  const [taskFilter, setTaskFilter] = useState('');
  const taskInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hoursInputRef = useRef<HTMLInputElement>(null);
  const projectSelectRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (entry) {
      setProjectId(entry.projectId);
      setDate(entry.date);
      setHours(entry.hours.toString());
      setDescription(entry.description || '');
      setTask(entry.task || '');
      setTaskDisplay(entry.task || '');
    } else {
      setProjectId(initialProjectId || 0);
      setDate(initialDate || '');
      // Use pre-filled values from paste if available
      setHours(initialHours !== undefined ? initialHours.toString() : '');
      setDescription(initialDescription || '');
      setTask(initialTask || '');
      setTaskDisplay(initialTask || '');
    }
    setError('');
    setMonthLocked(false);
    setAvailableTasks([]);
    setTasksProjectId(null);
    carriedTaskRef.current = null;
    setTaskFilter('');
  }, [entry, initialDate, initialProjectId, initialHours, initialDescription, initialTask, isOpen]);

  // Focus appropriate input when modal opens.
  // F20-002: capture the handle and clearTimeout on cleanup so a fast
  // close/unmount within the 50ms window doesn't fire .focus() on a
  // ref that may now be null or belong to a different rendered modal.
  useEffect(() => {
    if (!isOpen || entry) return;
    const handle = window.setTimeout(() => {
      if (initialDate && initialProjectId) {
        // Both date and project prefilled - focus hours
        hoursInputRef.current?.focus();
        hoursInputRef.current?.select();
      } else if (!initialProjectId) {
        // No project prefilled - focus project select
        projectSelectRef.current?.focus();
      }
    }, 50);
    return () => clearTimeout(handle);
  }, [isOpen, entry, initialDate, initialProjectId]);

  // Fetch tasks when project changes.
  // Use an ignore flag so rapid project switches (or unmount mid-flight)
  // can't land a stale response after a newer one and show the wrong
  // project's tasks. F18-002.
  useEffect(() => {
    if (projectId <= 0) {
      setAvailableTasks([]);
      setTasksProjectId(null);
      return;
    }
    let cancelled = false;
    getProjectTasks(projectId)
      .then((response) => {
        if (cancelled) return;
        setAvailableTasks(response.data);
        // MKS-220 — only a SUCCESSFUL load may stamp the owning project: a
        // failed fetch leaves an empty list that must never be read as
        // "this project has no such task" and clear the user's input.
        setTasksProjectId(projectId);
      })
      .catch(() => {
        if (!cancelled) setAvailableTasks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Phase 28 / MKS-115 — once tasks load, upgrade the closed-input display
  // from bare name to "#code — name" if we can match the prefilled task.
  useEffect(() => {
    if (!task || availableTasks.length === 0) return;
    if (taskDisplay !== task) return; // already upgraded or user typed
    const match = availableTasks.find((t) => t.name === task);
    if (match) {
      setTaskDisplay(formatTaskIdentifier(match));
    }
  }, [availableTasks, task, taskDisplay]);

  // MKS-220 — reconcile a task carried across a project switch. The dialog used
  // to keep the task string on the assumption the backend would resolve it in
  // the new project; it did not — it CREATED it there, leaving junk tasks on
  // the target project. The backend now only looks the task up, so the field
  // has to empty itself when the new project has no task of that name, to show
  // the user they must pick one. Deliberately an effect and not part of
  // handleProjectChange: the new project's task list arrives asynchronously.
  useEffect(() => {
    const carried = carriedTaskRef.current;
    // Nothing carried, or the list in hand is not yet the new project's.
    if (carried === null || tasksProjectId !== projectId) return;
    // The user typed or picked something since the switch — leave them alone.
    if (task !== carried) {
      carriedTaskRef.current = null;
      return;
    }
    carriedTaskRef.current = null;
    const match = availableTasks.find(
      (t) => t.name.toLowerCase() === carried.trim().toLowerCase()
    );
    if (match) {
      setTask(match.name);
      setTaskDisplay(formatTaskIdentifier(match));
    } else {
      setTask('');
      setTaskDisplay('');
    }
  }, [tasksProjectId, projectId, availableTasks, task]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsTaskDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close modal on Escape key
  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleProjectChange = (newProjectId: number) => {
    if (newProjectId === projectId) return;
    setProjectId(newProjectId);
    // MKS-220 — hand the current task to the reconcile effect above, which
    // keeps it only if the new project actually has a task of that name and
    // otherwise clears the field. Keeping it unconditionally made the backend
    // create the task on the target project.
    carriedTaskRef.current = task.trim() ? task : null;
    setTaskFilter('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMonthLocked(false);

    if (!projectId) {
      setError('Please select a project');
      return;
    }

    if (!date) {
      setError('Please select a date');
      return;
    }

    const hoursNum = parseDecimalInput(hours);
    if (hoursNum === null || hoursNum <= 0 || hoursNum > 24) {
      setError('Hours must be between 0 and 24');
      return;
    }

    // Check if task is required for this project
    const selectedProject = projects.find((p) => p.id === projectId);
    if (selectedProject?.mandatoryTasks && !task.trim()) {
      setError('Task is required for this project');
      return;
    }

    setIsLoading(true);
    try {
      await onSave({
        projectId,
        date,
        hours: hoursNum,
        description: description || undefined,
        task: task || undefined,
      });
      onClose();
    } catch (err) {
      // Phase 113 (MKS-208, SC3) — discriminate on the backend's machine-readable
      // `code`, never on the message text: the copy is going to be edited, and
      // the CTA must not quietly stop appearing when it is.
      if (err instanceof ApiClientError && err.code === PROJECT_MONTH_LOCKED_CODE) {
        setMonthLocked(true);
      }
      setError(err instanceof Error ? err.message : 'Failed to save entry');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    if (!confirm('Are you sure you want to delete this entry?')) {
      return;
    }

    setIsLoading(true);
    try {
      await onDelete();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete entry');
    } finally {
      setIsLoading(false);
    }
  };

  // Phase 28 / MKS-115 — picking a task: keep bare name in `task` (BE
  // contract) but show formatted `#code — name` in the input.
  const handleTaskPick = (t: ProjectTask) => {
    setTask(t.name);
    setTaskDisplay(formatTaskIdentifier(t));
    setTaskFilter('');
    setIsTaskDropdownOpen(false);
  };

  // "Add new task" path (free-text not yet in availableTasks): the typed
  // string IS the bare name and the display.
  const handleTaskAddNew = (typed: string) => {
    setTask(typed);
    setTaskDisplay(typed);
    setTaskFilter('');
    setIsTaskDropdownOpen(false);
  };

  const handleTaskInputChange = (value: string) => {
    // User is typing — keep bare name and display in sync. Filter against
    // the typed value.
    setTask(value);
    setTaskDisplay(value);
    setTaskFilter(value);
    if (!isTaskDropdownOpen) {
      setIsTaskDropdownOpen(true);
    }
  };

  // Filter tasks based on input (search by name and code)
  const filteredTasks = availableTasks.filter((t) => {
    const q = taskFilter.toLowerCase();
    return (t.name || '').toLowerCase().includes(q) || (t.code || '').toLowerCase().includes(q);
  });

  // Check if the current input matches any existing task (by name or code)
  const isNewTask = task.trim() !== '' && !availableTasks.some(
    (t) => (t.name || '').toLowerCase() === task.toLowerCase() || (t.code || '').toLowerCase() === task.toLowerCase()
  );

  if (!isOpen) return null;

  const selectedProject = projects.find((p) => p.id === projectId);
  const taskRequired = selectedProject?.mandatoryTasks;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-25"
          onClick={onClose}
        />

        <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">
              {entry ? 'Edit Time Entry' : 'New Time Entry'}
            </h3>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm space-y-2">
                <p>{error}</p>
                {monthLocked && onRequestBackdatedHours && (
                  <button
                    type="button"
                    onClick={() =>
                      onRequestBackdatedHours({
                        projectId,
                        date,
                        hours: parseDecimalInput(hours) ?? undefined,
                        task: task || null,
                        description: description || null,
                      })
                    }
                    className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                  >
                    Request backdated hours instead
                  </button>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Project
              </label>
              <select
                ref={projectSelectRef}
                value={projectId}
                onChange={(e) => handleProjectChange(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={0}>Select a project...</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.fullName || project.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date
              </label>
              <DateField
                value={date}
                onChange={setDate}
                disabled={lockedMonthMoveOnly}
                data-testid="entry-date"
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  lockedMonthMoveOnly ? 'bg-gray-100 text-gray-500' : ''
                }`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hours
              </label>
              <input
                ref={hoursInputRef}
                type="number"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                min="0"
                max="24"
                step="any"
                placeholder="0"
                disabled={lockedMonthMoveOnly}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  lockedMonthMoveOnly ? 'bg-gray-100 text-gray-500' : ''
                }`}
              />
              {/* MKS-536 — one line saying why the two frozen fields are
                  frozen. The month is still closed for ADDING and REMOVING
                  hours; only re-filing the ones already in it is allowed. */}
              {lockedMonthMoveOnly && (
                <p className="mt-1 text-xs text-amber-700">
                  This month is locked — you can move these hours to another task or project, but
                  not change the hours or the day.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="What did you work on?"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div ref={dropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Task {taskRequired ? <span className="text-red-500">*</span> : '(optional)'}
              </label>
              <div className="relative">
                <input
                  ref={taskInputRef}
                  type="text"
                  value={taskDisplay}
                  onChange={(e) => handleTaskInputChange(e.target.value)}
                  onFocus={() => setIsTaskDropdownOpen(true)}
                  placeholder="Select or type a task..."
                  className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    taskRequired && !task.trim() ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setIsTaskDropdownOpen(!isTaskDropdownOpen)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                >
                  <ChevronDownIcon className={`w-4 h-4 transition-transform ${isTaskDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isTaskDropdownOpen && projectId > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-auto">
                    {filteredTasks.length === 0 && !task.trim() && (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        No existing tasks. Type to add a new one.
                      </div>
                    )}
                    {filteredTasks.map((t) => (
                      <button
                        key={`${t.code}-${t.name}`}
                        type="button"
                        onClick={() => handleTaskPick(t)}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 ${
                          (t.name || '').toLowerCase() === task.toLowerCase() ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                        }`}
                      >
                        {formatTaskIdentifier(t)}
                      </button>
                    ))}
                    {isNewTask && (
                      <button
                        type="button"
                        onClick={() => handleTaskAddNew(task.trim())}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-green-50 text-green-700 border-t border-gray-100 flex items-center gap-2"
                      >
                        <PlusIcon className="w-4 h-4" />
                        Add &ldquo;{task.trim()}&rdquo;
                      </button>
                    )}
                  </div>
                )}
              </div>
              {taskRequired && (
                <p className="mt-1 text-xs text-gray-500">Required for this project</p>
              )}
              {(() => {
                const selectedTask = availableTasks.find(
                  (t) => t.name.toLowerCase() === task.toLowerCase() || t.code.toLowerCase() === task.toLowerCase()
                );
                if (!selectedTask) return null;
                return (
                  <>
                    {selectedTask.budget != null && (() => {
                      const spent = selectedTask.budgetSpent ?? 0;
                      const remaining = selectedTask.budget - spent;
                      const pct = selectedTask.budget > 0 ? (spent / selectedTask.budget) * 100 : 0;
                      const colorClass = pct > 100
                        ? 'text-red-600 bg-red-50'
                        : pct >= 80
                          ? 'text-yellow-700 bg-yellow-50'
                          : 'text-green-700 bg-green-50';
                      return (
                        <p className={`mt-1.5 text-xs px-2 py-1 rounded ${colorClass}`}>
                          Budget: {Math.round(remaining).toLocaleString()} CZK remaining ({Math.round(spent).toLocaleString()} / {Math.round(selectedTask.budget).toLocaleString()} CZK)
                        </p>
                      );
                    })()}
                    {selectedTask.plannedHours != null && (() => {
                      const actual = selectedTask.actualHours ?? 0;
                      const pct = selectedTask.plannedHours > 0 ? (actual / selectedTask.plannedHours) * 100 : 0;
                      const colorClass = pct > 100
                        ? 'text-red-600 bg-red-50'
                        : pct >= 80
                          ? 'text-yellow-700 bg-yellow-50'
                          : 'text-green-700 bg-green-50';
                      return (
                        <p className={`mt-1.5 text-xs px-2 py-1 rounded ${colorClass}`}>
                          Hours: {actual.toFixed(1)} / {selectedTask.plannedHours.toFixed(1)} planned
                        </p>
                      );
                    })()}
                  </>
                );
              })()}
            </div>

            <div className="flex justify-between pt-4">
              <div>
                {/* MKS-536 — deleting takes hours OUT of the locked month, so
                    it keeps needing a backdated-hours request; only the move is
                    exempt. */}
                {entry && onDelete && !lockedMonthMoveOnly && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={isLoading}
                    className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                  >
                    Delete
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* MKS-388 — say why Save is dead rather than leaving a greyed
                    button with no explanation. */}
                {saveDisabled && saveDisabledReason && (
                  <span className="text-xs text-amber-700">{saveDisabledReason}</span>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading || saveDisabled}
                  title={saveDisabled ? saveDisabledReason : undefined}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
